import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useAction } from 'convex/react'
import {
  ArrowLeftIcon,
  BookmarkIcon,
  ShareIcon,
  AdjustmentsHorizontalIcon,
  CodeBracketSquareIcon,
  StarIcon,
  PlusIcon,
  MinusIcon,
  LockClosedIcon,
  XMarkIcon,
  ChevronRightIcon,
  UserCircleIcon,
  ScissorsIcon,
  SparklesIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import { BookmarkIcon as BookmarkSolid, StarIcon as StarSolid } from '@heroicons/react/24/solid'
import { api } from '../convex_generated/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../contexts/ToastContext'
import { useUI } from '../contexts/UIContext'
import { useGenerationProgress } from '../lib/useGenerationProgress'
import SlateRenderer, { BranchMarker } from '../components/reader/SlateRenderer'
import FollowContentButton from '../components/social/FollowContentButton'
import { AddCommentModal, ViewCommentsModal, type ParagraphComment } from '../components/comments/CommentModals'
import { convex } from '../lib/convex'
import { AI_TIER_OPTIONS, type AiTier } from '../lib/aiTiers'
import { asId } from '../lib/ids'

// Extract flat text from a single Slate node
function getNodeText(node: unknown): string {
  if (!node || typeof node !== 'object') return ''
  const n = node as Record<string, unknown>
  if (typeof n.text === 'string') return n.text
  if (Array.isArray(n.children)) return (n.children as unknown[]).map(getNodeText).join('')
  return ''
}

// Build a Map<nodeIndex, BranchMarker[]> by matching each child's forkContext
// against the content nodes. Falls back to appending unmatched branches after the
// last node so they're never silently dropped.
function computeBranchMarkers(
  contentNodes: unknown[],
  children: Array<{ _id: string; title: string; category: string; wordCount: number; rating: number; forkContext?: string | null }>,
): Map<number, BranchMarker[]> {
  const map = new Map<number, BranchMarker[]>()

  const add = (idx: number, branch: BranchMarker) => {
    if (!map.has(idx)) map.set(idx, [])
    map.get(idx)!.push(branch)
  }

  const lastIdx = Math.max(0, contentNodes.length - 1)

  for (const child of children) {
    const marker: BranchMarker = {
      id: child._id,
      title: child.title,
      category: child.category,
      wordCount: child.wordCount,
      rating: child.rating,
    }

    const ctx = child.forkContext?.trim()
    if (!ctx) {
      add(lastIdx, marker)
      continue
    }

    const needle = ctx.toLowerCase().slice(0, 50)
    let found = false
    for (let i = 0; i < contentNodes.length; i++) {
      const text = getNodeText(contentNodes[i]).toLowerCase()
      if (text.includes(needle)) {
        add(i, marker)
        found = true
        break
      }
    }
    if (!found) add(lastIdx, marker)
  }

  return map
}

// ---------------------------------------------------------------------------
// Fork-from-selection popover
// ---------------------------------------------------------------------------
interface ForkPopoverProps {
  x: number
  y: number
  selectedText: string
  onFork: () => void
  onClose: () => void
}

const ForkPopover: React.FC<ForkPopoverProps> = ({ x, y, selectedText, onFork, onClose }) => {
  const width = 220
  const safeX = Math.max(8, Math.min(x - width / 2, window.innerWidth - width - 8))
  // Show above the selection; the caret is at y (top of selection)
  return (
    <div
      className="fixed z-[200] flex items-center gap-1 bg-gray-900 dark:bg-gray-800 border border-gray-700 rounded-xl shadow-2xl px-2 py-1.5 text-sm"
      style={{ left: safeX, top: y - 52, width }}
      onMouseDown={(e) => e.preventDefault()} // keep selection alive on click
    >
      {/* Arrow pointing down */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0" style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1f2937' }} />
      <ScissorsIcon className="w-4 h-4 text-primary-400 flex-shrink-0" />
      <button
        onMouseDown={(e) => { e.preventDefault(); onFork() }}
        className="flex-1 text-left text-xs font-semibold text-white truncate"
        title={selectedText}
      >
        Branch from selection
      </button>
      <button
        onMouseDown={(e) => { e.preventDefault(); onClose() }}
        className="text-gray-400 hover:text-white flex-shrink-0 p-0.5 rounded"
      >
        <XMarkIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Fork modal – title + category picker
// ---------------------------------------------------------------------------
type BranchCategory = 'backstory' | 'parallel-worlds' | 'expanded-reality' | 'general' | 'origin-story' | 'linked-branch'

const CATEGORY_DESCRIPTIONS: Record<BranchCategory, string> = {
  general: 'Continue or reimagine the story freely',
  backstory: 'Explore what happened before this moment',
  'parallel-worlds': 'Take the narrative into a different reality',
  'expanded-reality': 'Zoom into a side character, place, or event',
  'origin-story': 'The backstory of a character from the book',
  'linked-branch': 'A bridge connecting this story to another book',
}

interface ForkModalProps {
  forkContext: string
  balance: number
  onConfirm: (title: string, category: BranchCategory) => Promise<void>
  onAiGenerate: (title: string, category: BranchCategory, brief: string, tier: AiTier) => Promise<void>
  onClose: () => void
}

const ForkModal: React.FC<ForkModalProps> = ({ forkContext, balance, onConfirm, onAiGenerate, onClose }) => {
  const [mode, setMode] = useState<'manual' | 'ai'>('manual')
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState<BranchCategory>('general')
  const [brief, setBrief] = useState('')
  const [tier, setTier] = useState<AiTier>('standard')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const price = AI_TIER_OPTIONS.find((t) => t.id === tier)!.price
  const insufficient = balance < price
  const { label: progressLabel, elapsed } = useGenerationProgress(busy && mode === 'ai')

  const handleSubmit = async () => {
    if (!title.trim()) { setError('Please give your branch a title'); return }
    if (mode === 'ai') {
      if (!brief.trim()) { setError('Tell the AI what you want to happen'); return }
      if (insufficient) { setError(`You need ${price} StoryCoins (you have ${balance})`); return }
    }
    setBusy(true)
    setError(null)
    try {
      if (mode === 'ai') await onAiGenerate(title.trim(), category, brief.trim(), tier)
      else await onConfirm(title.trim(), category)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ScissorsIcon className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Create a Branch</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Mode toggle: write it yourself vs let AI write it */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { setMode('manual'); setError(null) }}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
              mode === 'manual'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-300'
            }`}
          >
            <ScissorsIcon className="w-4 h-4" /> Write it myself
          </button>
          <button
            onClick={() => { setMode('ai'); setError(null) }}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
              mode === 'ai'
                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-violet-300'
            }`}
          >
            <SparklesIcon className="w-4 h-4" /> Let AI write it
          </button>
        </div>

        {/* Fork context excerpt */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Branching from</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 italic line-clamp-3">"{forkContext}"</p>
        </div>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            Branch title
          </label>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && mode === 'manual' && handleSubmit()}
            placeholder='e.g. "What if Mina escaped?"'
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Branch type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(Object.entries(CATEGORY_DESCRIPTIONS) as [BranchCategory, string][]).map(([cat, desc]) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`text-left p-3 rounded-xl border-2 transition-all ${
                  category === cat
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                }`}
              >
                <p className={`text-xs font-semibold capitalize mb-0.5 ${
                  category === cat ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {cat.replace(/-/g, ' ')}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">{desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* AI-only controls */}
        {mode === 'ai' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                What should happen?
              </label>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Describe what you want the AI to write — events, twists, who's involved, the mood…"
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Quality
              </label>
              <div className="grid grid-cols-3 gap-2">
                {AI_TIER_OPTIONS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTier(t.id)}
                    className={`p-2.5 rounded-xl border-2 text-center transition-all ${
                      tier === t.id
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-violet-300'
                    }`}
                  >
                    <p className={`text-xs font-semibold ${tier === t.id ? 'text-violet-700 dark:text-violet-300' : 'text-gray-700 dark:text-gray-300'}`}>{t.label}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">{t.desc}</p>
                    <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mt-1">{t.price} SC</p>
                  </button>
                ))}
              </div>
            </div>

            <p className={`text-xs ${insufficient ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              Balance: {balance} StoryCoins{insufficient ? ' — not enough for this tier' : ''}. The result is a private draft you can edit before publishing. You won't be charged if generation fails.
            </p>
          </>
        )}

        {busy && mode === 'ai' && (
          <div className="flex items-center gap-2.5 rounded-xl bg-violet-50 dark:bg-violet-900/20 px-3 py-2.5">
            <span className="w-4 h-4 rounded-full border-2 border-violet-300 border-t-violet-600 animate-spin" />
            <span className="text-sm font-medium text-violet-700 dark:text-violet-300">{progressLabel}</span>
            <span className="ml-auto text-xs tabular-nums text-violet-400">{elapsed}s</span>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} disabled={busy} className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50">
            Cancel
          </button>
          {mode === 'ai' ? (
            <button
              onClick={handleSubmit}
              disabled={busy || insufficient}
              className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5"
            >
              <SparklesIcon className="w-4 h-4" />
              {busy ? 'Generating…' : `Generate · ${price} SC`}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={busy}
              className="flex-1 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {busy ? 'Creating…' : 'Create Branch'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// AI illustration tiers — keep in sync with convex/aiIllustration.ts ILLUSTRATION_TIERS.
type IllustrationTier = 'flash' | 'pro'
const ILLUSTRATION_TIERS: { id: IllustrationTier; label: string; price: number; desc: string }[] = [
  { id: 'flash', label: 'Flash', price: 40, desc: 'Fast, lower cost' },
  { id: 'pro', label: 'Pro', price: 90, desc: 'Highest quality' },
]

interface IllustrateModalProps {
  branchId: string
  bookId: string
  position: number
  balance: number
  onClose: () => void
  onDone: () => void
}

const IllustrateModal: React.FC<IllustrateModalProps> = ({ branchId, bookId, position, balance, onClose, onDone }) => {
  const [prompt, setPrompt] = useState('')
  const [loadingPrompt, setLoadingPrompt] = useState(true)
  const [tier, setTier] = useState<IllustrationTier>('flash')
  const [referenceId, setReferenceId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { label: progressLabel, elapsed } = useGenerationProgress(busy)
  const price = ILLUSTRATION_TIERS.find((t) => t.id === tier)!.price
  const insufficient = balance < price
  const references = useQuery(
    api.branches.getBookIllustrations,
    bookId ? { bookId: asId<"books">(bookId) } : 'skip',
  ) as Array<{ _id: string; url: string; alt: string }> | undefined

  // Prefill with an AI-suggested prompt from the surrounding text (free helper).
  useEffect(() => {
    let cancelled = false
    setLoadingPrompt(true)
    convex
      .action(api.aiIllustration.suggestIllustrationPrompt, { branchId: asId<"branches">(branchId), position })
      .then((r) => { if (!cancelled) setPrompt(r?.prompt ?? '') })
      .catch(() => { /* leave empty — user can write their own */ })
      .finally(() => { if (!cancelled) setLoadingPrompt(false) })
    return () => { cancelled = true }
  }, [branchId, position])

  const handleGenerate = async () => {
    if (!prompt.trim()) { setError('Describe what the illustration should show'); return }
    if (insufficient) { setError(`You need ${price} StoryCoins (you have ${balance})`); return }
    setBusy(true)
    setError(null)
    try {
      await convex.action(api.aiIllustration.generateIllustration, {
        branchId: asId<"branches">(branchId),
        position,
        prompt: prompt.trim(),
        tier,
        referenceIllustrationId: asId<"illustrations">((referenceId ?? undefined)),
      })
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PhotoIcon className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Illustrate this moment</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            Image prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            maxLength={2000}
            disabled={loadingPrompt}
            placeholder={loadingPrompt ? 'Suggesting a prompt from the story…' : 'Describe the scene to illustrate…'}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none disabled:opacity-60"
          />
          <p className="mt-1 text-[11px] text-gray-400">
            The AI suggested this from the preceding paragraphs — edit it or write your own.
          </p>
        </div>

        {/* Quality tier */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Quality</label>
          <div className="grid grid-cols-2 gap-2">
            {ILLUSTRATION_TIERS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTier(t.id)}
                className={`p-2.5 rounded-xl border-2 text-center transition-all ${
                  tier === t.id
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-violet-300'
                }`}
              >
                <p className={`text-xs font-semibold ${tier === t.id ? 'text-violet-700 dark:text-violet-300' : 'text-gray-700 dark:text-gray-300'}`}>{t.label}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">{t.desc}</p>
                <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mt-1">{t.price} SC</p>
              </button>
            ))}
          </div>
        </div>

        {/* Reference image — keep characters/style consistent */}
        {references && references.length > 0 && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Match a reference <span className="font-normal normal-case text-gray-400">(optional — for consistent characters)</span>
            </label>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => setReferenceId(null)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg border-2 text-[10px] font-medium flex items-center justify-center ${
                  referenceId === null ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300' : 'border-gray-200 dark:border-gray-700 text-gray-500'
                }`}
              >
                None
              </button>
              {references.map((r) => (
                <button
                  key={r._id}
                  onClick={() => setReferenceId(r._id)}
                  title={r.alt}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                    referenceId === r._id ? 'border-violet-500 ring-2 ring-violet-300' : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <img src={r.url} alt={r.alt} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}

        <p className={`text-xs ${insufficient ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          Balance: {balance} StoryCoins{insufficient ? ' — not enough' : ''}. You won't be charged if generation fails.
        </p>

        {busy && (
          <div className="flex items-center gap-2.5 rounded-xl bg-violet-50 dark:bg-violet-900/20 px-3 py-2.5">
            <span className="w-4 h-4 rounded-full border-2 border-violet-300 border-t-violet-600 animate-spin" />
            <span className="text-sm font-medium text-violet-700 dark:text-violet-300">{progressLabel}</span>
            <span className="ml-auto text-xs tabular-nums text-violet-400">{elapsed}s</span>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} disabled={busy} className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={busy || loadingPrompt || insufficient}
            className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5"
          >
            <SparklesIcon className="w-4 h-4" />
            {busy ? 'Generating…' : `Generate · ${price} SC`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type FontSize = 'sm' | 'base' | 'lg' | 'xl'
const FONT_SIZES: FontSize[] = ['sm', 'base', 'lg', 'xl']
type FontFamily = 'serif' | 'sans'

const FONT_SIZE_LABELS: Record<FontSize, string> = {
  sm: 'Small',
  base: 'Medium',
  lg: 'Large',
  xl: 'Extra Large',
}

const CATEGORY_STYLES: Record<string, string> = {
  general: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  backstory: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  'parallel-worlds': 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  'expanded-reality': 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  'origin-story': 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  'linked-branch': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
}

// ---------------------------------------------------------------------------
// Interactive star rating
// ---------------------------------------------------------------------------
const StarRatingInput: React.FC<{ value: number; onChange: (n: number) => void; disabled?: boolean }> = ({
  value,
  onChange,
  disabled,
}) => {
  const [hovered, setHovered] = useState(0)
  const display = hovered || value

  return (
    <div className="flex gap-1" role="group" aria-label="Rate this branch">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          disabled={disabled}
          onClick={() => onChange(s)}
          onMouseEnter={() => !disabled && setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          className={`transition-transform ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          aria-label={`${s} star${s > 1 ? 's' : ''}`}
        >
          {s <= display ? (
            <StarSolid className="w-8 h-8 text-amber-400" />
          ) : (
            <StarIcon className="w-8 h-8 text-gray-300 dark:text-gray-600" />
          )}
        </button>
      ))}
    </div>
  )
}

// Display-only star rating
const StarRatingDisplay: React.FC<{ rating: number; count: number }> = ({ rating, count }) => (
  <div className="flex items-center gap-1.5">
    <div className="flex">
      {[1, 2, 3, 4, 5].map((s) => (
        s <= Math.round(rating)
          ? <StarSolid key={s} className="w-4 h-4 text-amber-400" />
          : <StarIcon key={s} className="w-4 h-4 text-gray-300 dark:text-gray-600" />
      ))}
    </div>
    <span className="text-sm text-gray-500 dark:text-gray-400">
      {rating > 0 ? rating.toFixed(1) : '—'} {count > 0 && `(${count})`}
    </span>
  </div>
)

// ---------------------------------------------------------------------------
// Settings drawer
// ---------------------------------------------------------------------------
const SettingsDrawer: React.FC<{
  open: boolean
  onClose: () => void
  fontSize: FontSize
  onFontSize: (s: FontSize) => void
  fontFamily: FontFamily
  onFontFamily: (f: FontFamily) => void
}> = ({ open, onClose, fontSize, onFontSize, fontFamily, onFontFamily }) => {
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed top-20 right-2 sm:right-4 z-50 w-[min(16rem,calc(100vw-1rem))] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Reading settings</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Font size
          </p>
          <div className="grid grid-cols-4 gap-1">
            {(Object.keys(FONT_SIZE_LABELS) as FontSize[]).map((s) => (
              <button
                key={s}
                onClick={() => onFontSize(s)}
                className={`py-1.5 rounded text-xs font-medium transition-colors ${
                  fontSize === s
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {FONT_SIZE_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Typeface
          </p>
          <div className="grid grid-cols-2 gap-1">
            {(['serif', 'sans'] as FontFamily[]).map((f) => (
              <button
                key={f}
                onClick={() => onFontFamily(f)}
                className={`py-1.5 rounded text-xs font-medium capitalize transition-colors ${
                  fontFamily === f
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                } ${f === 'serif' ? 'font-serif' : 'font-sans'}`}
              >
                {f === 'serif' ? 'Serif' : 'Sans-serif'}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// ReadingPage
// ---------------------------------------------------------------------------
const ReadingPage: React.FC = () => {
  const { branchId } = useParams<{ branchId: string }>()
  const navigate = useNavigate()
  const { isSignedIn, user, canModerate } = useAuth()
  const { showToast } = useToast()
  const { openAuthModal, readingFontSize: fontSize, setReadingFontSize, readingFontFamily: fontFamilyRaw, setReadingFontFamily } = useUI()
  // ReadingFontFamily includes 'mono' but this reading page only supports serif/sans
  const fontFamily: FontFamily = fontFamilyRaw === 'sans' ? 'sans' : 'serif'
  const setFontSize = setReadingFontSize
  const setFontFamily = (f: FontFamily) => setReadingFontFamily(f)
  const [showSettings, setShowSettings] = useState(false)
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [userRating, setUserRating] = useState(0)
  const [isSavingRating, setIsSavingRating] = useState(false)

  const contentRef = useRef<HTMLDivElement>(null)
  const [contentNodes, setContentNodes] = useState<unknown[]>([])

  // Fork-from-selection state
  const [forkPopover, setForkPopover] = useState<{ x: number; y: number; text: string } | null>(null)
  const [forkModalContext, setForkModalContext] = useState<string | null>(null)
  const [illustratePosition, setIllustratePosition] = useState<number | null>(null)

  // Comment state
  const [addCommentState, setAddCommentState] = useState<{ paragraphIndex: number; replyTo?: { id: string; authorName: string } } | null>(null)
  const [viewCommentsParagraph, setViewCommentsParagraph] = useState<number | null>(null)

  // Convex queries
  const branch = useQuery(
    api.branches.getBranchForReading,
    branchId ? { branchId: asId<"branches">(branchId) } : 'skip',
  )
  const myRating = useQuery(
    api.branches.getMyRatingForBranch,
    branchId ? { branchId: asId<"branches">(branchId) } : 'skip',
  )
  const me = useQuery(api.users.getMe)
  const storyCoinBalance = me?.storyCoins ?? 0
  const illustrations = useQuery(
    api.branches.getIllustrationsForBranch,
    branchId ? { branchId: asId<"branches">(branchId) } : 'skip',
  )
  const canIllustrate = !!(
    isSignedIn && me && branch &&
    (branch.authorId === me._id ||
      (branch.collaboratorIds as string[] | undefined)?.includes(me._id))
  )
  const isInProgress = branch?.visibility === 'in-progress'
  const isPublishedBranch = branch?.visibility === 'public'
  const isPublicDomainBranch = isPublishedBranch && branch?.isImported
  // Comments on all published branches + in-progress; branching only on published (not in-progress)
  const commentsEnabled = isInProgress || isPublishedBranch
  const canBranchPerParagraph = isPublishedBranch
  const rawComments = useQuery(
    api.comments.getCommentsForBranch,
    branchId && commentsEnabled ? { branchId: asId<"branches">(branchId) } : 'skip',
  )
  const comments = useMemo(() => (rawComments ?? []) as ParagraphComment[], [rawComments])

  const addCommentMutation = useMutation(api.comments.addComment)
  const deleteCommentMutation = useMutation(api.comments.deleteComment)
  const flagCommentMutation = useMutation(api.comments.flagComment)
  const likeCommentMutation = useMutation(api.comments.likeComment)
  const dislikeCommentMutation = useMutation(api.comments.dislikeComment)
  const rateBranch = useMutation(api.branches.rateBranch)
  const purchaseBranch = useMutation(api.payments.purchaseBranch)
  const deleteIllustration = useMutation(api.branches.deleteIllustration)
  const fileReport = useMutation(api.reports.fileReport)
  const buyStoryCoins = useAction(api.stripe.createCheckoutSession)

  // Compute Map<paragraphIndex, count> for SlateRenderer — includes replies
  const commentCounts = useMemo(() => {
    if (!comments.length) return undefined
    const map = new Map<number, number>()
    for (const c of comments) {
      map.set(c.paragraphIndex, (map.get(c.paragraphIndex) ?? 0) + 1 + c.replies.length)
    }
    return map
  }, [comments])

  // Fetch Slate JSON – either from storage URL or inline content field
  const branchContentUrl = branch?.contentUrl
  const branchContent = branch?.content
  const branchChildrenRaw = branch?.children
  const branchChildren = useMemo(() => branchChildrenRaw ?? [], [branchChildrenRaw])
  useEffect(() => {
    if (branchContentUrl) {
      fetch(branchContentUrl)
        .then((r) => r.json())
        .then((nodes) => setContentNodes(nodes as unknown[]))
        .catch(() => setContentNodes([]))
    } else if (Array.isArray(branchContent) && branchContent.length > 0) {
      setContentNodes(branchContent)
    }
  }, [branchContentUrl, branchContent])

  // Sync rating from Convex
  useEffect(() => {
    if (myRating?.rating) setUserRating(myRating.rating)
  }, [myRating])

  // Text-selection → fork popover (mouse + touch) — only for published/public-domain, not in-progress
  useEffect(() => {
    const show = () => {
      if (isInProgress) { setForkPopover(null); return }
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || !contentRef.current) { setForkPopover(null); return }
      const selectedText = sel.toString().trim()
      if (selectedText.length < 10) { setForkPopover(null); return }
      // Only trigger when selection is inside the article
      if (!contentRef.current.contains(sel.anchorNode)) { setForkPopover(null); return }
      const range = sel.getRangeAt(0)
      const rect = range.getBoundingClientRect()
      // Position the toolbar centred above the selection's top edge (viewport coords for fixed)
      setForkPopover({ x: rect.left + rect.width / 2, y: rect.top, text: selectedText })
    }

    // On mobile, selectionchange fires continuously while dragging the handles.
    // We listen to both selectionchange (debounced) and mouseup/touchend.
    let debounce: ReturnType<typeof setTimeout>
    const onSelectionChange = () => {
      clearTimeout(debounce)
      debounce = setTimeout(show, 300)
    }
    const onPointerUp = () => { clearTimeout(debounce); setTimeout(show, 50) }
    const onPointerDown = (e: PointerEvent) => {
      // Dismiss if clicking outside the article (but not when tapping a button in the popover)
      if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
        setForkPopover(null)
      }
    }

    document.addEventListener('selectionchange', onSelectionChange)
    document.addEventListener('mouseup', onPointerUp)
    document.addEventListener('touchend', onPointerUp)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      clearTimeout(debounce)
      document.removeEventListener('selectionchange', onSelectionChange)
      document.removeEventListener('mouseup', onPointerUp)
      document.removeEventListener('touchend', onPointerUp)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [isInProgress])

  // Load bookmark from localStorage
  useEffect(() => {
    if (branchId) {
      setIsBookmarked(localStorage.getItem(`bookmark:${branchId}`) === 'true')
    }
  }, [branchId])

  // Scroll progress bar
  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement
      const progress = el.scrollTop / Math.max(1, el.scrollHeight - el.clientHeight)
      setScrollProgress(Math.min(1, Math.max(0, progress)))
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Font size step helpers
  const fontSizeIdx = FONT_SIZES.indexOf(fontSize)
  const decreaseFontSize = useCallback(() => {
    const idx = FONT_SIZES.indexOf(fontSize)
    if (idx > 0) setFontSize(FONT_SIZES[idx - 1])
  }, [fontSize, setFontSize])
  const increaseFontSize = useCallback(() => {
    const idx = FONT_SIZES.indexOf(fontSize)
    if (idx < FONT_SIZES.length - 1) setFontSize(FONT_SIZES[idx + 1])
  }, [fontSize, setFontSize])

  const toggleBookmark = useCallback(() => {
    if (!branchId) return
    const next = !isBookmarked
    setIsBookmarked(next)
    localStorage.setItem(`bookmark:${branchId}`, String(next))
    showToast({ message: next ? 'Bookmarked!' : 'Bookmark removed', type: 'success' })
  }, [branchId, isBookmarked, showToast])

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      showToast({ message: 'Link copied to clipboard', type: 'success' })
    } catch {
      showToast({ message: 'Could not copy link', type: 'error' })
    }
  }, [showToast])

  const handleRate = useCallback(
    async (rating: number) => {
      if (!isSignedIn) {
        openAuthModal()
        return
      }
      if (!branchId) return
      setUserRating(rating)
      setIsSavingRating(true)
      try {
        await rateBranch({ branchId: asId<"branches">(branchId), rating })
        showToast({ message: 'Rating saved!', type: 'success' })
      } catch (e) {
        showToast({ message: e instanceof Error ? e.message : 'Failed to save rating', type: 'error' })
        setUserRating(myRating?.rating ?? 0)
      } finally {
        setIsSavingRating(false)
      }
    },
    [isSignedIn, branchId, rateBranch, showToast, openAuthModal, myRating],
  )

  const handleForkFromSelection = useCallback(() => {
    const sel = window.getSelection()
    const selectedText = sel?.toString().trim() ?? ''
    if (!isSignedIn) { openAuthModal(); setForkPopover(null); return }
    setForkModalContext(selectedText)
    setForkPopover(null)
    window.getSelection()?.removeAllRanges()
  }, [isSignedIn, openAuthModal])

  const handleBranchFromParagraph = useCallback((nodeIndex: number) => {
    if (!isSignedIn) { openAuthModal(); return }
    // Extract plain text from that paragraph node as context
    const node = contentNodes[nodeIndex]
    const extractText = (n: unknown): string => {
      const loose = n as { text?: unknown; children?: unknown[] } | null
      if (typeof loose?.text === 'string') return loose.text
      if (loose && Array.isArray(loose.children)) return loose.children.map(extractText).join('')
      return ''
    }
    const context = extractText(node).trim().slice(0, 300)
    setForkModalContext(context || `Paragraph ${nodeIndex + 1}`)
  }, [isSignedIn, openAuthModal, contentNodes])

  const handleIllustrateFromParagraph = useCallback((nodeIndex: number) => {
    if (!isSignedIn) { openAuthModal(); return }
    setIllustratePosition(nodeIndex)
  }, [isSignedIn, openAuthModal])

  const handleRemoveIllustration = useCallback(async (illustrationId: string) => {
    if (!window.confirm('Remove this illustration?')) return
    try {
      await deleteIllustration({ illustrationId: asId<"illustrations">(illustrationId) })
      showToast({ message: 'Illustration removed', type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Failed to remove', type: 'error' })
    }
  }, [deleteIllustration, showToast])

  const handleReportIllustration = useCallback(async (illustrationId: string) => {
    if (!isSignedIn) { openAuthModal(); return }
    const details = window.prompt('Report this illustration — what’s wrong with it? (optional)')
    if (details === null) return // cancelled
    try {
      await fileReport({ illustrationId: asId<"illustrations">(illustrationId), reason: 'other', details: details || undefined })
      showToast({ message: 'Reported — thank you', type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Failed to report', type: 'error' })
    }
  }, [isSignedIn, openAuthModal, fileReport, showToast])

  const handlePurchase = useCallback(async () => {
    if (!isSignedIn) { openAuthModal(); return }
    if (!branchId) return
    try {
      const result = await purchaseBranch({ branchId: asId<"branches">(branchId) })
      if (result.alreadyOwned) {
        showToast({ message: 'You already own this branch', type: 'info' })
      } else {
        showToast({ message: 'Unlocked! Enjoy the story.', type: 'success' })
      }
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Purchase failed', type: 'error' })
    }
  }, [isSignedIn, branchId, purchaseBranch, showToast, openAuthModal])

  const handleForkConfirm = useCallback(
    async (title: string, category: BranchCategory) => {
      if (!branch || !forkModalContext) return
      const bId = branch.bookId
      const parentId = branch._id
      const branchIdNew = await convex.mutation(api.branches.createBranch, {
        bookId: bId,
        parentBranchId: parentId,
        title,
        category,
        visibility: 'private',
        forkContext: forkModalContext,
      })
      setForkModalContext(null)
      showToast({ message: 'Branch created! Opening editor…', type: 'success' })
      navigate(`/editor/${branchIdNew}`)
    },
    [branch, forkModalContext, navigate, showToast],
  )

  const handleAiGenerate = useCallback(
    async (title: string, category: BranchCategory, brief: string, tier: AiTier) => {
      if (!branch || !forkModalContext) return
      // A linked branch bridges two books — there is nothing for the AI to write from one fork point
      if (category === 'linked-branch') throw new Error('Linked branches can\'t be AI-generated')
      const bId = branch.bookId
      const parentId = branch._id
      // Throws on failure/refusal (user is refunded server-side); ForkModal surfaces the message.
      const result = await convex.action(api.ai.generateBranch, {
        bookId: bId,
        parentBranchId: parentId,
        title,
        brief,
        tier,
        category,
        forkContext: forkModalContext,
      })
      setForkModalContext(null)
      showToast({ message: 'Draft generated! Opening editor…', type: 'success' })
      navigate(`/editor/${result.branchId}`)
    },
    [branch, forkModalContext, navigate, showToast],
  )

  const branchMarkers = useMemo(() => {
    if (!branchChildren.length || !contentNodes.length) return undefined
    return computeBranchMarkers(contentNodes, branchChildren)
  }, [contentNodes, branchChildren])

  // Comment handlers — must be defined before any early returns (Rules of Hooks)
  const handleAddComment = useCallback(
    async (text: string, parentCommentId?: string) => {
      if (!branchId || addCommentState === null) return
      await addCommentMutation({
        branchId: asId<"branches">(branchId),
        paragraphIndex: addCommentState.paragraphIndex,
        text,
        parentCommentId: asId<"paragraph_comments">(parentCommentId),
      })
      showToast({ message: 'Comment posted!', type: 'success' })
      setAddCommentState(null)
      setViewCommentsParagraph(addCommentState.paragraphIndex)
    },
    [branchId, addCommentState, addCommentMutation, showToast],
  )

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      await deleteCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
      showToast({ message: 'Comment deleted', type: 'success' })
    },
    [deleteCommentMutation, showToast],
  )

  const handleFlagComment = useCallback(
    async (commentId: string) => {
      const result = await flagCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
      showToast({
        message: result?.alreadyFlagged ? 'Already flagged' : 'Comment flagged for review',
        type: result?.alreadyFlagged ? 'info' : 'success',
      })
    },
    [flagCommentMutation, showToast],
  )

  const handleLikeComment = useCallback(
    async (commentId: string) => {
      if (!isSignedIn) { openAuthModal(); return }
      await likeCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
    },
    [isSignedIn, likeCommentMutation, openAuthModal],
  )

  const handleDislikeComment = useCallback(
    async (commentId: string) => {
      if (!isSignedIn) { openAuthModal(); return }
      await dislikeCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
    },
    [isSignedIn, dislikeCommentMutation, openAuthModal],
  )

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------
  if (branch === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading story…</p>
        </div>
      </div>
    )
  }

  if (!branch) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-700 dark:text-gray-300 text-lg">Branch not found.</p>
        <button onClick={() => navigate(-1)} className="btn btn-primary">Go Back</button>
      </div>
    )
  }

  const isOwnBranch = user && user._id === branch.authorId
  const isAdmin = canModerate()
  // Delete permission: admins always; on user-owned branches, the branch author too
  const canDeleteComments = isAdmin || (!isPublicDomainBranch && !!isOwnBranch)
  const totalWords = branch.wordCount ?? 0
  const readingMinutes = Math.max(1, Math.round(totalWords / 250))

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Fork-from-selection popover */}
      {forkPopover && (
        <ForkPopover
          x={forkPopover.x}
          y={forkPopover.y}
          selectedText={forkPopover.text}
          onFork={handleForkFromSelection}
          onClose={() => setForkPopover(null)}
        />
      )}
      {/* Fork modal */}
      {forkModalContext && (
        <ForkModal
          forkContext={forkModalContext}
          balance={storyCoinBalance}
          onAiGenerate={handleAiGenerate}
          onConfirm={handleForkConfirm}
          onClose={() => setForkModalContext(null)}
        />
      )}
      {/* Illustrate modal */}
      {illustratePosition !== null && branchId && (
        <IllustrateModal
          branchId={branchId}
          bookId={branch.bookId}
          position={illustratePosition}
          balance={storyCoinBalance}
          onClose={() => setIllustratePosition(null)}
          onDone={() => {
            setIllustratePosition(null)
            showToast({ message: 'Illustration added!', type: 'success' })
          }}
        />
      )}
      {/* Add-comment modal */}
      {addCommentState !== null && (
        <AddCommentModal
          paragraphIndex={addCommentState.paragraphIndex}
          replyTo={addCommentState.replyTo}
          onSubmit={handleAddComment}
          onClose={() => setAddCommentState(null)}
        />
      )}
      {/* View-comments modal */}
      {viewCommentsParagraph !== null && (() => {
        const paraComments = comments.filter((c) => c.paragraphIndex === viewCommentsParagraph)
        return (
          <ViewCommentsModal
            paragraphIndex={viewCommentsParagraph}
            comments={paraComments}
            currentUserId={user?._id}
            isAuthor={canDeleteComments}
            onDelete={handleDeleteComment}
            onFlag={handleFlagComment}
            onLike={handleLikeComment}
            onDislike={handleDislikeComment}
            onClose={() => setViewCommentsParagraph(null)}
            onAddComment={(replyTo) => {
              setViewCommentsParagraph(null)
              setAddCommentState({ paragraphIndex: viewCommentsParagraph, replyTo })
            }}
          />
        )
      })()}
      {/* Reading progress bar */}
      <div className="fixed top-16 left-0 right-0 z-30 h-0.5 bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full bg-primary-500 transition-[width] duration-150"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      {/* Sticky header */}
      <div className="sticky top-16 z-20 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              {branch.book && (
                <Link
                  to={`/branches/${branch.book._id}`}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline truncate block"
                >
                  {branch.book.title}
                </Link>
              )}
              <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {branch.title}
              </h1>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <FollowContentButton targetType="branch" targetId={branchId!} compact />
            <button
              onClick={toggleBookmark}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
            >
              {isBookmarked ? (
                <BookmarkSolid className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              ) : (
                <BookmarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              )}
            </button>
            <button
              onClick={handleShare}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              aria-label="Share"
            >
              <ShareIcon className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-0.5 border border-gray-200 dark:border-gray-600 rounded-lg px-0.5">
              <button
                onClick={decreaseFontSize}
                disabled={fontSizeIdx === 0}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Decrease font size"
              >
                <MinusIcon className="w-4 h-4" />
              </button>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-6 text-center select-none" title={FONT_SIZE_LABELS[fontSize]}>
                A
              </span>
              <button
                onClick={increaseFontSize}
                disabled={fontSizeIdx === FONT_SIZES.length - 1}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Increase font size"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setShowSettings((s) => !s)}
              className={`p-2 rounded-lg transition-colors ${
                showSettings
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
              aria-label="Reading settings"
            >
              <AdjustmentsHorizontalIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Settings drawer */}
      <SettingsDrawer
        open={showSettings}
        onClose={() => setShowSettings(false)}
        fontSize={fontSize}
        onFontSize={setFontSize}
        fontFamily={fontFamily}
        onFontFamily={setFontFamily}
      />

      {/* Main reading column */}
      <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6">

        {/* Article header */}
        <header className="mb-10">
          {/* Category + visibility */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative group">
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize cursor-default ${
                  CATEGORY_STYLES[branch.category] ?? 'bg-gray-100 text-gray-700'
                }`}
              >
                {branch.category.replace(/-/g, ' ')}
              </span>
              {CATEGORY_DESCRIPTIONS[branch.category as BranchCategory] && (
                <div className="pointer-events-none absolute bottom-full left-0 mb-1.5 hidden group-hover:block z-20">
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                    {CATEGORY_DESCRIPTIONS[branch.category as BranchCategory]}
                  </div>
                  <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 ml-3 -mt-1" />
                </div>
              )}
            </div>
            {branch.visibility === 'paid' && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                <LockClosedIcon className="w-3 h-3" />
                {branch.price} StoryCoins
              </span>
            )}
            {branch.book?.isPublicDomain && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                Free
              </span>
            )}
            {branch.aiGenerated && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                title="This story was written with the help of an AI model."
              >
                {branch.aiAssisted ? 'AI-assisted' : 'AI-generated'}
              </span>
            )}
          </div>

          <h1
            className={`font-bold text-gray-900 dark:text-gray-50 leading-tight mb-4 ${
              fontFamily === 'serif' ? 'font-serif' : 'font-sans'
            } text-3xl sm:text-4xl`}
          >
            {branch.title}
          </h1>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-5">
            {branch.author && (
              <div className="flex items-center gap-2">
                {branch.author.avatar ? (
                  <img
                    src={branch.author.avatar}
                    alt={branch.author.displayName}
                    className="w-7 h-7 rounded-full object-cover"
                  />
                ) : (
                  <UserCircleIcon className="w-7 h-7 text-gray-400" />
                )}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  <Link
                    to={`/user/${branch.author.username}`}
                    className="hover:text-primary-600 dark:hover:text-primary-400 hover:underline"
                  >
                    {branch.author.displayName}
                  </Link>
                </span>
              </div>
            )}
            <span>·</span>
            <span>
              {totalWords.toLocaleString()} words · {readingMinutes} min read
            </span>
            {branch.rating > 0 && (
              <>
                <span>·</span>
                <StarRatingDisplay rating={branch.rating} count={branch.ratingCount} />
              </>
            )}
          </div>

          {/* Book breadcrumb */}
          {branch.book && (
            <div className="flex items-center gap-2 py-3 px-4 bg-gray-100 dark:bg-gray-800 rounded-xl text-sm">
              <CodeBracketSquareIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-gray-500 dark:text-gray-400">From</span>
              <Link
                to={`/branches/${branch.book._id}`}
                className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {branch.book.title}
              </Link>
              <span className="text-gray-400 dark:text-gray-500">by {branch.book.originalAuthor}</span>
            </div>
          )}

          {/* Origin story character banner */}
          {branch.category === 'origin-story' && branch.characterName && (
            <div className="mt-3 flex items-center gap-2 py-3 px-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl text-sm">
              <UserCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
              <span className="text-rose-700 dark:text-rose-300">
                Origin story of <span className="font-semibold">{branch.characterName}</span>
              </span>
            </div>
          )}
        </header>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-10">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <span className="text-gray-400 dark:text-gray-500 text-xl">✦</span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* ← Story content / paywall */}
        {/* `locked` is the server's verdict — it already accounts for purchases,
            authorship, collaborators and admins. */}
        {branch.locked ? (
          <div className="relative">
            {/* Blurred preview (first ~300px of content) */}
            <div className="select-none pointer-events-none" style={{ maxHeight: 300, overflow: 'hidden', filter: 'blur(4px)', opacity: 0.5 }}>
              <SlateRenderer content={contentNodes} fontSize={fontSize} fontFamily={fontFamily} />
            </div>
            {/* Paywall card */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 text-center max-w-sm mx-4">
                <LockClosedIcon className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Unlock this branch
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  {branch.title}
                </p>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mb-5">
                  {branch.price} StoryCoins
                </p>
                {isSignedIn ? (
                  <>
                    <button
                      onClick={handlePurchase}
                      className="w-full btn btn-primary py-3 text-base font-semibold"
                    >
                      Unlock for {branch.price} StoryCoins
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 mb-3">
                      Your balance: <span className="font-semibold">{user?.storyCoins?.toLocaleString() ?? 0} SC</span>
                    </p>
                    {user && (branch.price ?? 0) > (user.storyCoins ?? 0) && (
                      <button
                        onClick={async () => {
                          try {
                            const needed = (branch.price ?? 0) - (user.storyCoins ?? 0)
                            const suggestedAmount = Math.max(500, Math.ceil(needed / 100) * 100)
                            const { url } = await buyStoryCoins({ amount: suggestedAmount })
                            if (url) window.location.href = url
                          } catch (e) {
                            showToast({ message: e instanceof Error ? e.message : 'Failed to start purchase', type: 'error' })
                          }
                        }}
                        className="w-full py-2.5 text-sm font-medium text-center text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                      >
                        Buy StoryCoins
                      </button>
                    )}
                    {user && (branch.price ?? 0) <= (user.storyCoins ?? 0) ? null : (
                      <Link
                        to="/profile?tab=wallet"
                        className="block w-full mt-2 py-1.5 text-xs font-medium text-center text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Go to Wallet
                      </Link>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => openAuthModal()}
                    className="w-full btn btn-primary py-3 text-base font-semibold"
                  >
                    Sign in to unlock
                  </button>
                )}
                <p className="text-xs text-gray-400 mt-3">
                  1 StoryCoin ≈ £0.01
                </p>
              </div>
            </div>
          </div>
        ) : (
          <article ref={contentRef}>
            <SlateRenderer
              content={contentNodes}
              fontSize={fontSize}
              fontFamily={fontFamily}
              branchMarkers={branchMarkers}
              inProgress={commentsEnabled}
              commentCounts={commentCounts}
              onAddComment={(nodeIndex) => {
                if (!isSignedIn) { openAuthModal(); return }
                setAddCommentState({ paragraphIndex: nodeIndex })
              }}
              onViewComments={(nodeIndex) => setViewCommentsParagraph(nodeIndex)}
              onBranchFromParagraph={canBranchPerParagraph ? handleBranchFromParagraph : undefined}
              onIllustrateFromParagraph={canIllustrate ? handleIllustrateFromParagraph : undefined}
              illustrations={illustrations ?? undefined}
              onRemoveIllustration={(canIllustrate || isAdmin) ? handleRemoveIllustration : undefined}
              onReportIllustration={(!canIllustrate && !isAdmin && isSignedIn) ? handleReportIllustration : undefined}
            />
          </article>
        )}

        {/* Divider */}
        <div className="flex items-center gap-4 mt-14 mb-10">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <span className="text-gray-400 dark:text-gray-500 text-xl">✦</span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* Rating section */}
        <section className="mb-10 bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Enjoyed this branch?
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            {isSignedIn
              ? isOwnBranch
                ? 'You cannot rate your own branch.'
                : 'Leave a rating to help other readers discover it.'
              : 'Sign in to rate this branch.'}
          </p>
          <div className="flex justify-center">
            <StarRatingInput
              value={userRating}
              onChange={handleRate}
              disabled={!isSignedIn || !!isOwnBranch || isSavingRating}
            />
          </div>
          {userRating > 0 && (
            <p className="mt-3 text-sm text-primary-600 dark:text-primary-400 font-medium">
              You rated this {userRating}/5 ★
            </p>
          )}
          {!isSignedIn && (
            <button
              onClick={openAuthModal}
              className="mt-4 px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Sign in to rate
            </button>
          )}
        </section>

        {/* Linked Branch — "Continue to target book" CTA */}
        {branch.category === 'linked-branch' && branch.targetBook && (
          <div className="mb-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-2xl p-5 border border-cyan-200 dark:border-cyan-800">
            <div className="flex-1">
              <p className="text-[10px] font-semibold text-cyan-500 uppercase tracking-wide mb-1">Bridge destination</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                Continue to "{branch.targetBook.title}"
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                by {branch.targetBook.originalAuthor}
              </p>
            </div>
            <Link
              to={`/branches/${branch.targetBook._id}${branch.targetForkContext ? `?scrollTo=${encodeURIComponent(branch.targetForkContext)}` : ''}`}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-xl transition-colors flex-shrink-0"
            >
              <span>⇌</span>
              Continue reading
            </Link>
          </div>
        )}

        {/* Fork CTA */}
        {isSignedIn && (
          <div className="mb-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 rounded-2xl p-5 border border-primary-200 dark:border-primary-800">
            <div className="flex-1">
              <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                Take the story in your own direction
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Fork this branch and write an alternative narrative.
              </p>
            </div>
            <Link
              to={`/editor?bookId=${branch.bookId}&parentId=${branch._id}`}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-colors flex-shrink-0"
            >
              <PlusIcon className="w-4 h-4" />
              Fork this branch
            </Link>
          </div>
        )}

        {/* Child branches – "Continue reading" */}
        {branch.children?.length > 0 && (
          <section className="mb-10">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
              Continue the story ({branch.children.length})
            </h2>
            <div className="space-y-3">
              {branch.children.map((child) => (
                <Link
                  key={child._id}
                  to={`/reading/${child._id}`}
                  className="group flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold capitalize ${
                          CATEGORY_STYLES[child.category] ?? 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {child.category.replace('-', ' ')}
                      </span>
                      {child.visibility === 'paid' && (
                        <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-semibold">
                          <LockClosedIcon className="w-2.5 h-2.5" />
                          {child.price} SC
                        </span>
                      )}
                      {child.aiGenerated && (
                        <span
                          className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 font-semibold"
                          title="Written with the help of an AI model."
                        >
                          <SparklesIcon className="w-2.5 h-2.5" />
                          AI
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors text-sm truncate">
                      {child.title}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {child.wordCount.toLocaleString()} words
                      {child.rating > 0 && ` · ★ ${child.rating.toFixed(1)}`}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary-500 flex-shrink-0 transition-colors" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Edit own branch */}
        {isOwnBranch && (
          <div className="mb-10 text-center">
            <Link
              to={`/editor/${branch._id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl transition-colors border border-gray-200 dark:border-gray-600"
            >
              Edit this branch
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReadingPage
